import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { FaEye, FaTrashAlt, FaTimes, FaSearch, FaChevronDown, FaChevronUp, FaFileExport } from 'react-icons/fa';
import { BsCheck2All } from 'react-icons/bs';
import { Helmet } from "react-helmet-async";
import './ViewContact.css';
import axiosInstance from '../../api/axiosInstance';
import Pagination from '../Pagination/Pagination';

const ContactViewModal = ({ contact, onClose }) => {
    if (!contact) return null;
    const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'short' }) : 'N/A';
    
    return (
        <div className="view-contact-modal-overlay" onClick={onClose}>
            <div className="view-contact-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="view-contact-modal-header">
                    <h2>Contact Submission Details</h2>
                    <button className="view-contact-modal-close-btn" onClick={onClose} aria-label="Close Details"><FaTimes /></button>
                </div>
                <div className="view-contact-modal-body">
                    <div className="view-contact-modal-grid">
                        <div className="view-contact-detail-item">
                            <label>Full Name</label>
                            <span>{contact.name}</span>
                        </div>
                        <div className="view-contact-detail-item">
                            <label>Status</label>
                            <div>
                                <span className={`view-contact-status-badge ${contact.status.toLowerCase().replace(' ', '-')}`}>
                                    {contact.status}
                                </span>
                            </div>
                        </div>
                        <div className="view-contact-detail-item">
                            <label>Email Address</label>
                            <span className="view-contact-text-break">{contact.email}</span>
                        </div>
                        <div className="view-contact-detail-item">
                            <label>Contact Number</label>
                            <span>{contact.phone}</span>
                        </div>
                        <div className="view-contact-detail-item">
                            <label>Submitted On</label>
                            <span>{formatDate(contact.createdAt)}</span>
                        </div>
                        {contact.resolvedOn && (
                            <div className="view-contact-detail-item">
                                <label>Resolved On</label>
                                <span>{formatDate(contact.resolvedOn)}</span>
                            </div>
                        )}
                        <div className="view-contact-detail-item view-contact-full-width">
                            <label>Comment / Message</label>
                            <div className="view-contact-text-content-modal">{contact.comment}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

const ViewContact = () => {
    const [contacts, setContacts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [expandedRow, setExpandedRow] = useState(null);
    const [viewingContact, setViewingContact] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const fetchContacts = async () => {
        try {
            const res = await axiosInstance.get('/api/contact/all');
            setContacts(res.data);
        } catch (error) {
            if (error.response?.status !== 401) console.error(error);
        }
    };

    useEffect(() => { fetchContacts(); }, []);

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));
    const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

    const filteredContacts = useMemo(() => {
        return contacts.filter(item => {
            if (statusFilter !== 'All' && item.status !== statusFilter) return false;
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                return item.name.toLowerCase().includes(q) || item.email.toLowerCase().includes(q) || item.phone.includes(q);
            }
            return true;
        });
    }, [contacts, searchQuery, statusFilter]);

    useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter]);

    const paginatedContacts = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredContacts.slice(start, start + itemsPerPage);
    }, [filteredContacts, currentPage, itemsPerPage]);

    const handleExportExcel = async () => {
        if (filteredContacts.length === 0) return Swal.fire('Notice', 'No data to export.', 'info');

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Contact Submissions');

        worksheet.mergeCells('A1:G1');
        worksheet.getCell('A1').value = 'RAJPUT SHIKSHA KOSH';
        worksheet.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFB91C1C' } };
        worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

        worksheet.mergeCells('A2:G2');
        worksheet.getCell('A2').value = `Contact Submissions Report (${statusFilter})`;
        worksheet.getCell('A2').font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FF2C3E50' } };
        worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

        worksheet.addRow([]);

        const headerRow = worksheet.addRow(['S.No', 'Name', 'Email Address', 'Contact', 'Comment', 'Status', 'Submitted On']);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
            cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        filteredContacts.forEach((c, i) => {
            const row = worksheet.addRow([ i + 1, c.name, c.email, c.phone, c.comment, c.status, formatDate(c.createdAt) ]);
            row.eachCell((cell) => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            });
            row.getCell(1).alignment = { horizontal: 'center' };
            
            const statusCell = row.getCell(6);
            statusCell.alignment = { horizontal: 'center' };
            statusCell.font = { bold: true, color: { argb: c.status === 'Resolved' ? 'FF16A34A' : c.status === 'In Progress' ? 'FF2563EB' : 'FFE67E22' } };
        });

        worksheet.columns = [ { width: 8 }, { width: 25 }, { width: 30 }, { width: 18 }, { width: 45 }, { width: 15 }, { width: 22 } ];

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'Contact_Submissions_Report.xlsx');
    };

    const handleAction = async (type, contact) => {
        if (type === 'view') {
            if (contact.status === 'Open') {
                try {
                    await axiosInstance.put(`/api/contact/update/${contact._id}`, { status: 'In Progress' });
                    fetchContacts(); 
                } catch(e) {}
            }
            setViewingContact(contact);
        }
        else if (type === 'resolve') {
            if (contact.status !== 'In Progress') {
                return Swal.fire('Notice', 'You must view the message first before resolving it.', 'info');
            }
            const res = await Swal.fire({ title: 'Mark as Resolved?', text: 'Move the query to resolved status.', icon: 'question', showCancelButton: true, confirmButtonColor: '#16a34a' });
            if (res.isConfirmed) {
                try {
                    await axiosInstance.put(`/api/contact/update/${contact._id}`, { status: 'Resolved' });
                    fetchContacts();
                    Swal.fire('Resolved', 'Status updated successfully.', 'success');
                } catch (error) { Swal.fire('Error', 'Update failed', 'error'); }
            }
        }
        else if (type === 'delete') {
            const res = await Swal.fire({ title: 'Delete entry?', text: 'This action cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e74c3c' });
            if (res.isConfirmed) {
                try {
                    await axiosInstance.delete(`/api/contact/delete/${contact._id}`);
                    fetchContacts();
                    Swal.fire('Deleted', 'Entry removed.', 'success');
                } catch (error) { Swal.fire('Error', 'Delete failed', 'error'); }
            }
        }
    };

    return (
        <>
        <Helmet>
                    <title>View Contacts | Rajput Shiksha Kosh</title>
            
                    <meta
                      name="description"
                      content="Manage and view contact submissions for the Rajput Shiksha Kosh organization."
                    />
                  </Helmet>
        <div className="view-contact-container">
            <ContactViewModal contact={viewingContact} onClose={() => setViewingContact(null)} />
            
            <h2 className="view-contact-title">Contact Submissions</h2>

            <div className="view-contact-controls">
                <div className="view-contact-search-wrapper">
                    <FaSearch className="view-contact-search-icon" />
                    <input type="text" className="view-contact-search-input" placeholder="Search Name, Email, Contact..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>
                
                <div className="view-contact-action-group">
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="view-contact-limit-select">
                        <option value="All">All Status</option>
                        <option value="Open">Open</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                    </select>
                    <select className="view-contact-limit-select" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                        {PAGE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt} / page</option>)}
                    </select>
                    <button className="view-contact-btn" onClick={handleExportExcel} title="Export to Excel">
                        <FaFileExport /> Export
                    </button>
                </div>
            </div>

            <div className="view-contact-table-wrapper">
                <table className="view-contact-table">
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th className="view-contact-align-left">Submitter Name</th>
                            <th className="view-contact-hide-tab view-contact-align-left">Email Address</th>
                            <th className="view-contact-hide-mob">Contact</th>
                            <th className="view-contact-hide-desk">Submitted On</th>
                            <th className="view-contact-col-status">Status</th>
                            <th className="view-contact-col-action">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedContacts.length === 0 ? (
                            <tr><td colSpan="7" className="view-contact-no-data">No contact submissions found.</td></tr>
                        ) : (
                            paginatedContacts.map((c, i) => {
                                const globalIndex = (currentPage - 1) * itemsPerPage + i;
                                return (
                                <React.Fragment key={c._id}>
                                    <tr className={`view-contact-main-row ${c.status === 'Open' ? 'unread' : ''}`}>
                                        <td>{globalIndex + 1}</td>
                                        <td className="view-contact-align-left">
                                            <div className="view-contact-name-wrapper" onClick={() => toggleRow(c._id)}>
                                                <div><span style={{fontWeight: 600}}>{c.name}</span></div>
                                                <button className="view-contact-expand-btn">{expandedRow === c._id ? <FaChevronUp /> : <FaChevronDown />}</button>
                                            </div>
                                        </td>
                                        <td className="view-contact-hide-tab view-contact-align-left">{c.email}</td>
                                        <td className="view-contact-hide-mob">{c.phone}</td>
                                        <td className="view-contact-hide-desk">{formatDate(c.createdAt)}</td>
                                        <td className="view-contact-col-status">
                                            <span className={`view-contact-status-badge ${c.status.toLowerCase().replace(' ', '-')}`}>{c.status}</span>
                                        </td>
                                        <td className="view-contact-col-action">
                                            <div className="view-contact-action-btns">
                                                <button className="view-contact-icon-btn view" title="View Details" onClick={() => handleAction('view', c)}><FaEye /></button>
                                                {c.status === 'In Progress' && (
                                                    <button className="view-contact-icon-btn resolve" title="Mark Resolved" onClick={() => handleAction('resolve', c)}><BsCheck2All /></button>
                                                )}
                                                {c.status === 'Resolved' && (
                                                    <button className="view-contact-icon-btn delete" title="Delete" onClick={() => handleAction('delete', c)}><FaTrashAlt /></button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedRow === c._id && (
                                        <tr className="view-contact-expanded-row">
                                            <td colSpan="7">
                                                <div className="view-contact-expanded-content">
                                                    <div className="view-contact-exp-item view-contact-show-tab">
                                                        <span className="view-contact-exp-label">Email Address</span><span className="view-contact-exp-value">{c.email}</span>
                                                    </div>
                                                    <div className="view-contact-exp-item view-contact-show-mob">
                                                        <span className="view-contact-exp-label">Contact Number</span><span className="view-contact-exp-value">{c.phone}</span>
                                                    </div>
                                                    <div className="view-contact-exp-item view-contact-show-desk">
                                                        <span className="view-contact-exp-label">Submitted On</span><span className="view-contact-exp-value">{formatDate(c.createdAt)}</span>
                                                    </div>
                                                    <div className="view-contact-exp-item view-contact-col-span-full">
                                                        <span className="view-contact-exp-label">Comment / Message</span><span className="view-contact-text-content">{c.comment}</span>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            )})
                        )}
                    </tbody>
                </table>
            </div>

            {filteredContacts.length > 0 && (
                <Pagination totalItems={filteredContacts.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
            )}
        </div>
        </>
    );
};

export default ViewContact;